/** @jsxImportSource theme-ui */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Text } from "theme-ui";
import styled from "@emotion/styled";
import Dropdown from "../atoms/Dropdown";
import { colors, typography } from "../../styles/theme";

const useScrollSpy = (sectionIds) => {
  const [activeSection, setActiveSection] = useState("");
  const observerRef = useRef(null);

  useEffect(() => {
    if (!sectionIds || sectionIds.length === 0) return undefined;
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -70% 0px",
      threshold: 0,
    };
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
          if (typeof window !== "undefined") {
            window.history.replaceState(null, "", `#${entry.target.id}`);
          }
        }
      });
    };
    observerRef.current = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );
    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observerRef.current.observe(element);
      }
    });
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sectionIds]);

  return [activeSection, setActiveSection];
};

const StyledTOCNav = styled.nav`
  position: sticky;
  top: 100px;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  li {
    margin: 0;
  }
  a {
    position: relative;
    display: block;
    padding: 0.5rem 0 0.5rem 1rem;
    color: ${colors.neutral800};
    text-decoration: none;
    font-size: 0.938rem;
    line-height: 1.5;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 4px;
      background-color: ${colors.neutral200};
      border-radius: 2px;
      transition: background-color 0.25s ease;
    }
    &.active {
      color: ${colors.primaryMain};
      font-weight: 500;

      &::before {
        background-color: ${colors.primaryMain};
      }
    }
  }

  a.toc-back-to-top {
    padding-left: 0;
    &::before {
      display: none;
    }
  }
`;

const mobileTocDropdownContainerStyles = {
  display: ["block", "block", "block", "none"],
  position: "sticky",
  top: "4.375rem",
  zIndex: 100,
  backgroundColor: colors.white,
  borderBottom: `1px solid ${colors.neutral200}`,
  pt: "1rem",
  pb: "1rem",
  px: "1rem",
};

const mobileTocDropdownCustomStyles = {
  control: {
    height: "auto",
    minHeight: "3rem",
    border: `1px solid ${colors.neutral200}`,
    borderRadius: "0.75rem",
    padding: "0.5rem 1rem",
    backgroundColor: colors.white,
  },
  placeholder: {
    ...typography.h6,
    color: colors.neutral900,
  },
  singleValue: {
    ...typography.textMd500,
    color: colors.primaryMain,
  },
  option: {
    ...typography.textMd400,
    padding: "1rem",
    borderLeft: `3px solid transparent`,
    "&:hover": {
      backgroundColor: colors.primary100,
    },
  },
  menu: {
    borderRadius: "0.75rem",
    border: `1px solid ${colors.neutral200}`,
    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.08)",
    marginTop: "0.5rem",
  },
  dropdownIndicator: {
    color: colors.primaryMain,
  },
};

/**
 * Desktop sticky table of contents.
 * @param {{ id: string, title: string }[]} sections
 */
export const TableOfContents = ({ sections, title = "In this Article" }) => {
  const sectionIds = sections?.map((s) => s.id) || [];
  const [activeSection, setActiveSection] = useScrollSpy(sectionIds);

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(sectionId);
      if (typeof window !== "undefined") {
        window.history.pushState(null, "", `#${sectionId}`);
      }
    }
  };

  const scrollToTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (typeof window !== "undefined") {
      window.history.pushState(null, "", window.location.pathname);
    }
  };

  if (!sections?.length) return null;

  return (
    <StyledTOCNav>
      <Text
        sx={{
          ...typography.h6,
          mb: "1rem",
          display: "block",
          color: colors.neutral900,
        }}
      >
        {title}
      </Text>
      <ul style={{ ...typography.textMd500, color: colors.neutral900 }}>
        {sections.map((section, index) => (
          <li key={`toc-item-${index}`}>
            <a
              href={`#${section.id}`}
              onClick={(e) => scrollToSection(e, section.id)}
              className={activeSection === section.id ? "active" : ""}
            >
              {section.title}
            </a>
          </li>
        ))}
      </ul>
      <Box sx={{ mt: "1.5rem" }}>
        <a
          href="#top"
          className="toc-back-to-top"
          onClick={scrollToTop}
          style={{
            ...typography.h6,
            color: colors.primaryMain,
            textDecoration: "none",
          }}
        >
          Back to top
        </a>
      </Box>
    </StyledTOCNav>
  );
};

TableOfContents.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ),
  title: PropTypes.string,
};

/**
 * Mobile/tablet sticky TOC dropdown.
 */
export const MobileTOC = ({
  sections,
  placeholder = "In this Article",
  customContainerStyles = {},
}) => {
  const sectionIds = sections?.map((s) => s.id) || [];
  const [activeSection] = useScrollSpy(sectionIds);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 160;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      if (typeof window !== "undefined") {
        window.history.pushState(null, "", `#${sectionId}`);
      }
    }
  };

  const handleDropdownChange = (selectedOption) => {
    if (selectedOption?.value) {
      scrollToSection(selectedOption.value);
    }
  };

  if (!sections?.length) return null;

  const dropdownOptions = sections.map((section) => ({
    value: section.id,
    label: section.title,
  }));

  const activeOption = activeSection
    ? dropdownOptions.find((opt) => opt.value === activeSection)
    : null;

  return (
    <Box
      sx={{
        ...mobileTocDropdownContainerStyles,
        ...customContainerStyles,
      }}
    >
      <Dropdown
        options={dropdownOptions}
        placeholder={placeholder}
        onChange={handleDropdownChange}
        value={activeOption}
        customStyles={mobileTocDropdownCustomStyles}
      />
    </Box>
  );
};

MobileTOC.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ),
  placeholder: PropTypes.string,
  customContainerStyles: PropTypes.object,
};

export { useScrollSpy };
